import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  BottomSheet,
  Button,
  Chip,
  Notice,
  Segmented,
  TextField,
  useSnackbar,
  DateField,
} from '@sauda/ui/kit';
import { ApiError, api } from '../../lib/api.js';
import { istDateInput } from '../../lib/money-input.js';
import { formatRupees, rupeesToPaise } from '../../lib/money.js';

const MODES = ['upi', 'cash', 'bank', 'cheque'] as const;

/** Records one receipt of brokerage against a deal. The balance is shown so a typo is easy to see. */
export function ReceiptSheet({
  dealId,
  balancePaise,
  title,
  open,
  onClose,
}: {
  dealId: string | null;
  balancePaise: number;
  title: string;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const cache = useQueryClient();
  const snack = useSnackbar();
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(istDateInput());
  const [mode, setMode] = useState<(typeof MODES)[number]>('upi');
  const [payer, setPayer] = useState<'tenant' | 'owner'>('tenant');
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);
  const paise = rupeesToPaise(amount);
  const save = useMutation({
    mutationFn: () =>
      api(`/deals/${dealId ?? ''}/receipts`, {
        method: 'POST',
        body: {
          amountPaise: paise,
          receivedOn: date,
          mode,
          payer,
          ...(reference.trim() === '' ? {} : { reference: reference.trim() }),
        },
      }),
    onSuccess: async () => {
      setAmount('');
      setReference('');
      setError(null);
      await Promise.all([
        cache.invalidateQueries({ queryKey: ['brokerage'] }),
        cache.invalidateQueries({ queryKey: ['deal'] }),
        cache.invalidateQueries({ queryKey: ['deals'] }),
      ]);
      onClose();
      snack({ text: t('receipt.saved', { amount: formatRupees(paise ?? 0) }) });
    },
    onError: (e) =>
      setError(
        e instanceof ApiError && e.code === 'OVER_RECEIPT' ? t('receipt.over') : t('deal.generic'),
      ),
  });
  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      label={t('receipt.record')}
      title={t('receipt.record')}
      closeLabel={t('app.close')}
      actions={
        <Button
          size="lg"
          block
          loading={save.isPending}
          disabled={paise === null || paise <= 0 || date === ''}
          onClick={() => save.mutate()}
        >
          {t('receipt.save')}
        </Button>
      }
    >
      <div className="pub-form k-stack">
        <p className="k-t-body k-muted">{title}</p>
        <TextField
          label={t('receipt.amount')}
          money
          prefix="₹"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          supporting={t('receipt.balance', { amount: formatRupees(balancePaise) })}
        />
        <DateField label={t('receipt.date')} value={date} onChange={setDate} />
        <div className="chips-row" role="group" aria-label={t('receipt.mode')}>
          {MODES.map((m) => (
            <Chip key={m} selected={mode === m} onClick={() => setMode(m)}>
              {t(`deals.mode_${m}`)}
            </Chip>
          ))}
        </div>
        <span className="k-t-label-lg">{t('receipt.payer')}</span>
        <Segmented
          label={t('receipt.payer')}
          value={payer}
          onChange={(v) => setPayer(v as 'tenant' | 'owner')}
          options={[
            { value: 'tenant', label: t('deals.party_tenant') },
            { value: 'owner', label: t('deals.party_owner') },
          ]}
        />
        <TextField
          label={t('receipt.reference')}
          value={reference}
          onChange={(e) => setReference(e.target.value)}
        />
        {error === null ? null : <Notice tone="error">{error}</Notice>}
      </div>
    </BottomSheet>
  );
}
